import { ModalWrapper } from '../base/ModalWrapper';
import { useState, useCallback, useMemo } from 'react';
import { DashboardCard } from '../base/DashboardCard';
import EmptyMessageCard from '../base/EmptyMessageCard';
import MilestoneCard from './MilestoneCard';
import { Empty } from '@/components/common/empty';
import { useMilestones } from '@/store/milestones/hooks';
import type { Milestone } from '@/store/milestones/types';
import { loadPerson } from '@/store/people/actions/people';
import { usePeopleStore } from '@/store/people';
import { useApiWithAuth } from '@/hooks/api';
import { useDrawerActions } from '@/store/drawer';
import type { Person } from '@/store/people/types';

export function UpcomingMilestones() {
  const limit = 5;
  const { loading, upcoming, active, excluded } = useMilestones();
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);

  const sublist = useMemo(() => upcoming.slice(0, limit), [upcoming, limit]);
  const peopleStore = usePeopleStore();
  const { api, auth } = useApiWithAuth();
  const {
    setOpen: setDrawerIsOpen,
    setCurrentPerson,
    setCurrentTab,
  } = useDrawerActions();

  const handleViewAll = useCallback(() => {
    setIsViewAllOpen(true);
  }, []);

  const handleCloseViewAll = useCallback(() => {
    setIsViewAllOpen(false);
  }, []);

  const handleItemClick = useCallback(
    (item: Milestone) => {
      loadPerson(peopleStore, {
        api,
        auth,
        id: item.data.profile_id,
        force: true,
        cache: false,
      })
        .then((result) => {
          setDrawerIsOpen(true);

          if (!result.exists) {
            console.warn('Person does not exist');
          } else if (result.exists && result.loaded) {
            setCurrentPerson(result.item as Person);
            setCurrentTab('milestones');
          }

          return;
        })
        .catch(console.error);
    },
    [api, auth, peopleStore, setDrawerIsOpen, setCurrentPerson, setCurrentTab],
  );

  // User doesn't have any oversight for setting criteria to load
  // so we'll just show nothing instead of loading all.
  // This could be adjusted as the rules are further refined.
  if (excluded) {
    return null;
  }

  return (
    <>
      <DashboardCard loading={loading}>
        <DashboardCard.Header
          title='Milestones'
          subtitle='upcoming milestones'
          count={active.length}
          onViewAll={handleViewAll}
        />
        <DashboardCard.Body
          empty={upcoming.length === 0}
          emptyContent={
            <EmptyMessageCard
              icon='mdi:event-outline'
              message="You're all caught up!"
            />
          }
        >
          {sublist.map((item) => (
            <div key={item.id}>
              <MilestoneCard
                className='w-full'
                data={item.data}
                onPress={() => handleItemClick(item)}
              />
            </div>
          ))}
        </DashboardCard.Body>
      </DashboardCard>

      <ModalWrapper
        isOpen={isViewAllOpen}
        onClose={handleCloseViewAll}
        title='Upcoming Milestones'
        size='lg'
      >
        <div className='space-y-2'>
          {!active.length && (
            <Empty
              description="You're all caught up"
              icon='mdi:event-outline'
            />
          )}
          {active.map((item) => (
            <div key={item.id} className='cursor-pointer'>
              <MilestoneCard
                data={item.data}
                onPress={() => handleItemClick(item)}
              />
            </div>
          ))}
        </div>
      </ModalWrapper>
    </>
  );
}
